import { useEffect, useRef, useState } from "react";
import { Room, RoomEvent, Track } from "livekit-client";

// --- GLOBAL FETCH PATCH TO KILL LIVEKIT /REGIONS LOOP ---
// The livekit-client SDK aggressively polls region metrics in the background
// for .livekit.cloud URLs, even continuing to ping endpoints after component unmounts!
// By patching window.fetch globally once, we ensure it intercepts these requests 
// continuously, silently satisfying the SDK with a direct local loopback response.
if (typeof window !== "undefined" && !window._livekitFetchPatched) {
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const fetchUrl = typeof args[0] === "string" ? args[0] : args[0]?.url;
    if (fetchUrl && fetchUrl.includes("/regions") && fetchUrl.includes(".livekit.cloud")) {
      const baseUrl = fetchUrl.split("/regions")[0].replace("https://", "wss://").replace("http://", "ws://");
      return new Response(JSON.stringify([{ region: "default", url: baseUrl, distance: 0 }]), {
          status: 200,
          headers: { "Content-Type": "application/json" }
      });
    }
    return originalFetch(...args);
  };
  window._livekitFetchPatched = true;
}

/**
 * LiveKitVideoPlayer
 *
 * Connects to a LiveKit room using the provided credentials and renders
 * the remote video track (HeyGen avatar stream) into a <video> element.
 *
 * Props:
 *   livekitUrl  - wss://... URL from avatarSession.livekit_url
 *   token       - JWT from avatarSession.livekit_client_token
 *   onError     - optional callback when connection fails
 *   onConnected - optional callback when the stream successfully connects to the avatar
 */
const LiveKitVideoPlayer = ({ livekitUrl, token, onError, onConnected }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const roomRef = useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | connected | error

  // Use refs to store the latest callbacks to avoid triggering useEffect re-runs
  const callbacksRef = useRef({ onError, onConnected });
  useEffect(() => {
    callbacksRef.current = { onError, onConnected };
  });

  useEffect(() => {
    if (!livekitUrl || !token) return;

    let isMounted = true;
    let room = null;

    // 1) Pre-validate token via REST to prevent livekit-client from infinitely looping region discovery on 404s
    const httpUrl = livekitUrl.replace("wss://", "https://").replace("ws://", "http://");
    const validateUrl = `${httpUrl}/rtc/validate?access_token=${token}`;

    fetch(validateUrl)
      .then(async (res) => {
        if (!isMounted) return;
        
        if (!res.ok) {
          console.error("LiveKit pre-validation failed:", res.status);
          setStatus("error");
          if (callbacksRef.current.onError) callbacksRef.current.onError(new Error("Invalid or expired LiveKit token"));
          return;
        }

        // 2) If validation succeeds, ONLY THEN initialize Room and connect
        room = new Room({
          adaptiveStream: false,
          dynacast: false,
          manageEngineLifecycle: true,
          webAudioMix: false,
          disconnectOnPageLeave: true,
        });
        roomRef.current = room;

        const handleTrackSubscribed = (track, publication, participant) => {
          if (track.kind === Track.Kind.Video && videoRef.current) {
            track.attach(videoRef.current);
            setStatus("connected");
            if (callbacksRef.current.onConnected) callbacksRef.current.onConnected();
          } else if (track.kind === Track.Kind.Audio && audioRef.current) {
            track.attach(audioRef.current);
          }
        };

        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        room.on(RoomEvent.Disconnected, () => {
          if (isMounted) setStatus("error");
        });

        if (room.engine && room.engine.regionUrlProvider) {
           room.engine.regionUrlProvider.getNextBestRegionUrl = async () => null;
        }

        try {
          await room.connect(livekitUrl, token, {
            autoSubscribe: true,
            maxRetries: 0
          });
          
          if (!isMounted) {
            room.disconnect();
            return;
          }

          console.log("LiveKit room connected:", room.name);
          room.remoteParticipants.forEach((participant) => {
            participant.trackPublications.forEach((publication) => {
              if (publication.isSubscribed) {
                if (publication.track?.kind === Track.Kind.Video && videoRef.current) {
                  publication.track.attach(videoRef.current);
                  if (isMounted) {
                    setStatus("connected");
                    if (callbacksRef.current.onConnected) callbacksRef.current.onConnected();
                  }
                } else if (publication.track?.kind === Track.Kind.Audio && audioRef.current) {
                  publication.track.attach(audioRef.current);
                }
              }
            });
          });
        } catch (err) {
          console.error("LiveKit connection error:", err);
          if (isMounted) setStatus("error");
          room.disconnect();
          if (callbacksRef.current.onError) callbacksRef.current.onError(err);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Pre-validation fetch error:", err);
        setStatus("error");
        if (callbacksRef.current.onError) callbacksRef.current.onError(err);
      });

    return () => {
      isMounted = false;
      if (room) room.disconnect();
    };
  }, [livekitUrl, token]);

  return (
    <section className="livekit_video_wrapper">
      {status === "connecting" && (
        <div className="livekit_connecting_overlay">
          <div className="live_avatar_spinner"></div>
          <p>Connecting to Live Avatar...</p>
        </div>
      )}
      {status === "error" && (
        <div className="livekit_error_overlay">
          <p>⚠️ Could not connect to avatar stream.</p>
        </div>
      )}
      <video
        ref={videoRef}
        autoPlay
        playsInline
                        controlsList="nodownload"

        muted={false}
        className={`livekit_video ${status === "connected" ? "visible" : "hidden"}`}
      />
      <audio ref={audioRef} autoPlay />
    </section>
  );
};

export default LiveKitVideoPlayer;
